import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('tt_token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // GoogleAuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    if (token) {
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, user } = response.data;
    localStorage.setItem('tt_token', access_token);
    setToken(access_token);
    setUser(user);
    return user;
  };

  const loginWithGoogle = async (sessionId, referralCode = null) => {
    const payload = { session_id: sessionId };
    if (referralCode) payload.referral_code = referralCode;
    const response = await axios.post(`${API}/auth/google-callback`, payload);
    const { access_token, user } = response.data;
    localStorage.setItem('tt_token', access_token);
    setToken(access_token);
    setUser(user);
    return user;
  };

  const register = async (email, username, password, isTeacher = false, dateOfBirth = null, referralCode = null) => {
    const payload = { email, username, password, is_teacher: isTeacher, date_of_birth: dateOfBirth };
    if (referralCode) payload.referral_code = referralCode;
    const response = await axios.post(`${API}/auth/register`, payload);
    const { access_token, user } = response.data;
    localStorage.setItem('tt_token', access_token);
    setToken(access_token);
    setUser(user);
    return user;
  };

  const logout = () => {
    localStorage.removeItem('tt_token');
    setToken(null);
    setUser(null);
    window.location.href = '/';
  };

  const value = {
    user,
    token,
    loading,
    login,
    loginWithGoogle,
    register,
    logout,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
